import type { ReactNode, RefObject } from 'react'

import { BadlandMark } from '@/components/BadlandLogo'
import type { SceneMode } from '@/scene/controller'

interface GateFrameProps {
  /** The scene's state (theme.css hands the drawn mark over on `live`); `shell` before the scene exists. */
  mode: SceneMode | 'shell'
  /** What draws behind the page: the ember scene, or the bare sky while it loads. */
  scene: ReactNode
  kicker?: ReactNode
  /** The page's h1, set huge in the display face. */
  title: ReactNode
  lede?: ReactNode
  children: ReactNode
  /** Receives the mark's box, the origin of the iris into the dashboard after sign-in. */
  anchorRef?: RefObject<HTMLDivElement | null>
}

/**
 * The sign-in pages' frame without the ember scene, so the gate can be drawn
 * from the app's first chunk: GateShell (the wait while the session is
 * checked, also index.html's first paint) and GateLayout (the live pages)
 * share it, and their title, mark and card land on the same pixels.
 */
export function GateFrame({ mode, scene, kicker, title, lede, children, anchorRef }: GateFrameProps) {
  return (
    <div data-ember={mode} className="relative h-dvh overflow-y-auto overflow-x-hidden overscroll-none bg-[color:var(--bl-canvas)]">
      {scene}

      <div className="relative z-10 mx-auto flex min-h-full w-full max-w-7xl flex-col px-5 pb-[max(env(safe-area-inset-bottom),calc(var(--spacing)*5))] pt-[max(env(safe-area-inset-top),calc(var(--spacing)*2))] sm:px-10 lg:px-16">
        <div className="flex items-center gap-3 py-4 sm:py-6">
          <BadlandMark className="h-8 w-8 text-[color:var(--bl-gold)] drop-shadow-[0_0_10px_rgb(var(--color-primary)/0.5)]" />
          <span className="font-[family-name:var(--bl-font-display)] text-xl font-extrabold uppercase tracking-[0.18em] text-[color:var(--bl-gold-text)]">Badland</span>
          <span aria-hidden="true" className="h-4 w-px bg-[color:var(--bl-line-strong)]" />
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[color:var(--bl-ink-3)]">Dashboard</span>
        </div>

        <main className="flex flex-1 flex-col justify-center gap-7 py-4 sm:gap-9 lg:max-w-sm lg:py-10 xl:max-w-lg">
          <div ref={anchorRef} className="bl-gate-anchor">
            <BadlandMark className="bl-emblem block h-full w-full" />
          </div>

          <div className="space-y-4 text-center lg:text-left">
            {kicker ? <p className="bl-kicker">{kicker}</p> : null}
            <h1 className="bl-gate-title">{title}</h1>
            {lede ? <p className="mx-auto max-w-md text-[length:calc(var(--text-base)*1.02)] leading-relaxed text-[color:var(--bl-ink-2)] lg:mx-0">{lede}</p> : null}
          </div>

          <div className="bl-gate-card bl-panel-lit w-full p-5 sm:p-7">{children}</div>
        </main>

        <footer className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 pt-6 text-xs text-[color:var(--bl-ink-3)] lg:justify-start">
          <span>© Badland</span>
          <a href="https://badland.ai/privacy" className="transition-colors hover:text-[color:var(--bl-gold-text)]">Privacy</a>
          <a href="https://badland.ai/terms" className="transition-colors hover:text-[color:var(--bl-gold-text)]">Terms</a>
          <a href="mailto:support@badland.ai" className="transition-colors hover:text-[color:var(--bl-gold-text)]">support@badland.ai</a>
        </footer>
      </div>
    </div>
  )
}
